import React, { useState, useEffect, useMemo } from 'react';
import { updateAccount } from '../../_shared/wishlist/wishlistUtils';
import { CustomerData } from '../../_shared/types';
import { globalWindow } from 'src/scripts/utils/constants';
import {
  phoneNumberFormatter,
  formatPhoneNumber,
  removeCountryCode,
  checkIsValid,
} from '../../_shared/ui-components/SignInModal/utils';
import { usePromise } from '../../_shared/react-hooks';
import { isInGeotarget } from 'src/scripts/utils/location';
import { ShippingCountryState } from 'src/scripts/utils/shipping';
import FormInput from '../../_shared/ui-components/YogaUI/FormInput';
import { MAX_NAME_LENGTH } from 'src/scripts/utils/constants';

const customerData = window.getCustomerData?.();

const AccountInformation = () => {
  const [customerInfo, setCustomerInfo] = useState<CustomerData>({
    first_name: customerData?.firstName,
    last_name: customerData?.lastName,
    email: customerData?.email,
    accepts_email_marketing: customerData?.acceptsEmailMarketing,
    phone: customerData?.phone ? formatPhoneNumber(removeCountryCode(customerData.phone)) : '',
  });

  const [formState, setFormState] = useState({
    FirstName: checkIsValid(customerInfo?.first_name || '', '^(?!\\s*$).+'),
    LastName: checkIsValid(customerInfo?.last_name || '', '^(?!\\s*$).+'),
    Email: checkIsValid(customerInfo?.email || '', globalWindow?.emailRegex),
  });
  const update = 'Update Account';
  const [updateTxt, setUpdateTxt] = useState(update);
  const [hasUpdate, setHasUpdate] = useState(false);
  const [phoneNumberError, setPhoneNumberErrorState] = useState(false);
  const phoneNumberTooltipMessage = 'Enables faster account lookup in-store.';
  const { data: isInUS } = usePromise(() => isInGeotarget({ countries: ['US'] }));
  const isDomestic = ShippingCountryState.countryCode ? ShippingCountryState.countryCode === 'US' : isInUS;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleChange = (event: any) => {
    if (event.target.name === 'accepts_email_marketing') {
      setCustomerInfo({ ...customerInfo, [event.target.name]: event.target.checked });
    } else {
      setCustomerInfo({ ...customerInfo, [event.target.name]: event.target.value });
    }
  };
  useEffect(() => {
    if (hasUpdate) {
      setTimeout(() => {
        setHasUpdate(false);
      }, 2000);
    }
    return () => {};
  }, [hasUpdate]);

  const handleUpdate = async () => {
    if (isAccountInfoValid) {
      setUpdateTxt('Updating');
      const updateResponse = await updateAccount({
        ...customerInfo,
        phone: customerInfo?.phone ? customerInfo.phone.replace(/[^\d]/g, '') : '',
      });
      if (updateResponse.ok && updateResponse.status === 200) {
        setUpdateTxt(update);
        setHasUpdate(true);
      } else {
        setUpdateTxt(update);
        console.log('Something went wrong with updating account: ', updateResponse);
      }
    }
  };

  const isAccountInfoValid = useMemo(() => {
    return formState.FirstName && formState.LastName && formState.Email;
  }, [formState.FirstName, formState.LastName, formState.Email]);

  return (
    <>
      {' '}
      <div className="headings">
        <h3 className="account-info h3 uppercase title border-bottom-ac">Account Info</h3>
      </div>
      <div className="form-group form-group-placeholder margin g-info">
        <h4 className="h4 uppercase semibold">General Info</h4>
      </div>
      <div id="customer-fields">
        <div className="form-group">
          <div className="form-group form-group-placeholder">
            <FormInput
              options={{
                id: 'FirstName',
                wrapperClasses: '',
                type: 'text',
                required: true,
                errorText: 'Please enter your first name',
                label: 'First Name',
                name: 'first_name',
                pattern: '^(?!\\s*$).+',
                handleFormState: (isValid) => {
                  setFormState({ ...formState, FirstName: isValid });
                },
                defaultValue: customerInfo?.first_name,
                maxLength: MAX_NAME_LENGTH,
                onChange: handleChange,
              }}
            />
          </div>
          <div className="form-group form-group-placeholder">
            <FormInput
              options={{
                id: 'LastName',
                wrapperClasses: '',
                type: 'text',
                required: true,
                errorText: 'Please enter your last name',
                label: 'Last Name',
                name: 'last_name',
                pattern: '^(?!\\s*$).+',
                handleFormState: (isValid) => {
                  setFormState({ ...formState, LastName: isValid });
                },
                defaultValue: customerInfo.last_name,
                maxLength: MAX_NAME_LENGTH,
                onChange: handleChange,
              }}
            />
          </div>
          <div className="form-group form-group-placeholder">
            <FormInput
              options={{
                id: 'Email',
                wrapperClasses: '',
                type: 'email',
                required: true,
                errorText: 'Please enter a valid email',
                label: 'Email',
                pattern: globalWindow?.emailRegex,
                name: 'email',
                handleFormState: (isValid) => {
                  setFormState({ ...formState, Email: isValid });
                },
                defaultValue: customerInfo.email,
                maxLength: MAX_NAME_LENGTH,
                onChange: handleChange,
              }}
            />
          </div>

          {isDomestic && (
            <div className="form-group form-group-placeholder">
              <div className={`yoga-ui-text-input ${phoneNumberError ? 'error-border' : ''}`}>
                <input
                  type="tel"
                  name="phone"
                  id="PhoneNumber"
                  className=" "
                  placeholder="Phone number (optional)"
                  spellCheck="false"
                  autoComplete="tel"
                  autoCapitalize="off"
                  value={customerInfo.phone}
                  onChange={(e) => {
                    phoneNumberFormatter(e);
                    handleChange(e);
                  }}
                  onBlur={(e) => {
                    const pattern = /^\(\d{3}\)\s\d{3}[- ]{1}\d{4}$/;
                    if (e.currentTarget.value === '') {
                      setPhoneNumberErrorState(false);
                    } else {
                      setPhoneNumberErrorState(!pattern.test(e.currentTarget.value));
                    }
                  }}
                  onFocus={(e) => {
                    setPhoneNumberErrorState(false);
                  }}
                />
                <label htmlFor="PhoneNumber" className="">
                  Phone number
                </label>
                {phoneNumberError ? (
                  <span className="error error-input-form">Please enter a valid US number</span>
                ) : null}
                <div className="tooltip">
                  <div className="more-info-tooltip-icon">
                    <img src="https://cdn.shopify.com/s/files/1/2185/2813/files/icon.svg?v=1675189680" alt="Tooltip" />
                  </div>
                  <span className="tooltip-text">{phoneNumberTooltipMessage}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      <div className="button-group shopping update-button">
        <button
          className="button-primary btn-primary btn-block mb-3"
          onClick={() => handleUpdate()}
          disabled={!isAccountInfoValid}
        >
          {updateTxt}
        </button>
        {hasUpdate && (
          <div className="success-mark">
            <div className="circle">
              <div className="checkMark"></div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};
export default AccountInformation;
